import { useEffect } from 'react';
import { useStore } from '@nanostores/react';
import { hydraUiApi } from '@/services/api';
import { getOrCreateSignupAnonymousId } from './anonymous-id';
import { signupPublicFlagsStore, type SignupPublicFlags } from './public-flags-store';

/**
 * Public-rollout bucketing prefixes the anonymous id so signup buckets
 * independently of other public feature-flag surfaces.
 */
const SIGNUP_PUBLIC_FLAG_ANONYMOUS_ID_PREFIX = 'signup';

let inflight: Promise<void> | null = null;

/**
 * Resolve the signup public flags exactly once per app load. A failed fetch
 * settles the store to `{}` (every flag off), so signup can never block or
 * hang on this endpoint: all flags off means the embedded app serves the
 * funnel, which is the safe default. The store only ever transitions
 * `null → resolved`, so consumers that decide between apps on it can't flip
 * after their first decision.
 */
export function ensureSignupPublicFlags(): Promise<void> {
  if (signupPublicFlagsStore.get() != null) {
    return Promise.resolve();
  }
  inflight ??= fetchSignupPublicFlags();
  return inflight;
}

async function fetchSignupPublicFlags(): Promise<void> {
  try {
    const data = await hydraUiApi.featureFlags.publicFeatureFlags({
      body: { anonymous_id: `${SIGNUP_PUBLIC_FLAG_ANONYMOUS_ID_PREFIX}:${getOrCreateSignupAnonymousId()}` },
    });
    signupPublicFlagsStore.set(data.feature_flags ?? {});
  } catch {
    signupPublicFlagsStore.set({});
  }
}

/** Subscribe to the signup public flags, kicking off the fetch on first use. */
export function useSignupPublicFlags(): SignupPublicFlags {
  useEffect(() => {
    void ensureSignupPublicFlags();
  }, []);
  return useStore(signupPublicFlagsStore);
}

/** Test-only: clear the resolved flags and the in-flight memo. */
export function resetSignupPublicFlags(): void {
  inflight = null;
  signupPublicFlagsStore.set(null);
}
